'use client';

import { Lock, ServerOff, Fingerprint, LockIcon, EyeOff } from 'lucide-react';
import FadeIn from '../ui/FadeIn';
import SecurityRadar from '../ui/SecurityRadar';
import { useTranslations } from '@/lib/i18n/TranslationProvider';

export default function Security() {
    const { t } = useTranslations();
    const icons = [Fingerprint, LockIcon, ServerOff, EyeOff];

    const protocols = t.security.protocols.map((p, i) => ({
        icon: icons[i],
        title: p.title,
        desc: p.desc,
    }));

    return (
        <section id="security" className="section-padding bg-[var(--color-md-surface-variant)] relative overflow-hidden">

            <div className="container mx-auto px-6 relative z-10">
                <div className="flex flex-col lg:flex-row items-center gap-16">

                    {/* Visual Side: Security Radar (Replaces Wheel) */}
                    <div className="flex-1 relative flex items-center justify-center min-h-[500px] w-full max-w-xl">
                        {/* Scale up the radar for this large section */}
                        <div className="scale-[1.6]">
                            <SecurityRadar />
                        </div>

                        {/* Floor Shadow */}
                        <div className="absolute bottom-10 w-64 h-8 bg-black/[0.05] blur-xl rounded-[100%]" />
                    </div>

                    {/* Text Side */}
                    <div className="flex-1">
                        <FadeIn direction="up" yOffset={40}>
                            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline)] shadow-cinematic-1 text-[var(--color-md-on-surface)] font-bold text-xs mb-8">
                                <div className="w-6 h-6 m3-icon-tonal flex-center rounded-full">
                                    <Lock className="w-3.5 h-3.5" />
                                </div>
                                {t.security.badge}
                            </div>
                            <h2 className="text-4xl md:text-6xl font-black text-[var(--color-md-on-surface)] tracking-tight mb-10 leading-tight">
                                {t.security.heading} <span className="text-[var(--color-md-primary)]">{t.security.headingAccent}</span> {t.security.headingEnd}
                            </h2>
                            <p className="text-xl text-[var(--color-md-on-surface-variant)] leading-relaxed mb-12 font-bold antialiased">
                                {t.security.subtitle} <br className="hidden md:block" />
                                <span className="text-[var(--color-md-on-surface)]">{t.security.subtitleBold}</span> {t.security.subtitleEnd}
                            </p>
                        </FadeIn>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {protocols.map((p, i) => (
                                <FadeIn key={i} delay={i * 0.1} direction="up" yOffset={20}>
                                    <div className="bg-[var(--color-md-surface)] rounded-[32px] p-8 md:p-10 border border-[var(--color-md-outline)] shadow-cinematic-1 hover:shadow-cinematic-3 hover:border-[var(--color-md-outline-variant)] transition-all duration-500 hover:-translate-y-1 group">
                                        <div className="flex items-center gap-4 mb-6">
                                            <div className={`w-12 h-12 flex-shrink-0 ${
                                                i % 3 === 0 ? 'm3-icon-tonal' : 
                                                i % 3 === 1 ? 'm3-icon-secondary' : 
                                                'm3-icon-tertiary'
                                            }`}>
                                                <p.icon className="w-6 h-6" />
                                            </div>
                                            <h3 className="font-black text-[var(--color-md-on-surface)] tracking-tight">{p.title}</h3>
                                        </div>
                                        <p className="text-base text-[var(--color-md-on-surface-variant)] leading-relaxed font-bold">
                                            {p.desc}
                                        </p>
                                    </div>
                                </FadeIn>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
}
